import { CartErrorEvent, DiscountUpdateEvent } from './events.js';
import { cartPerformance } from './performance-component.js';

const selectors = {
  component: '[data-cart-discount]',
  error: '[data-cart-discount-error]',
  form: 'form[data-cart-discount-form]',
  input: 'input[name="discount"]',
  pill: '[data-cart-discount-pill]',
  section: '[data-cart-drawer-section], [data-cart-page-section]',
};

const routesRoot = window.Shopify?.routes?.root || '/';
const activeControllers = new WeakMap();

function cartSectionIds() {
  return Array.from(document.querySelectorAll(selectors.section))
    .map((section) => section.dataset.sectionId)
    .filter(Boolean)
    .filter((sectionId, index, sectionIds) => sectionIds.indexOf(sectionId) === index);
}

function cartSectionsPayload() {
  const sectionIds = cartSectionIds();
  if (sectionIds.length === 0) return {};

  const sectionsUrl = new URL(window.location.href);
  sectionsUrl.searchParams.delete('sections');
  sectionsUrl.searchParams.delete('section_id');

  return {
    sections: sectionIds.join(','),
    sections_url: `${sectionsUrl.pathname}${sectionsUrl.search}`,
  };
}

function existingDiscounts(component) {
  return Array.from(component.querySelectorAll(selectors.pill))
    .map((pill) => pill.dataset.discountCode)
    .filter(Boolean);
}

function setError(component, message) {
  if (!component) return;
  const error = component.querySelector(selectors.error);
  if (!error) return;

  error.hidden = !message;
  error.textContent = message || '';
}

function currentComponent(sourceId, fallback) {
  const current = document.getElementById(sourceId);
  if (current?.matches(selectors.component)) return current;
  return fallback?.isConnected ? fallback : null;
}

function setBusy(component, busy) {
  component.toggleAttribute('aria-busy', busy);
  component.querySelectorAll('button, input').forEach((control) => {
    control.toggleAttribute('disabled', busy);
  });
}

function abortPrevious(component) {
  activeControllers.get(component)?.abort();
  const controller = new AbortController();
  activeControllers.set(component, controller);
  return controller;
}

function clearController(component, controller) {
  if (activeControllers.get(component) === controller) {
    activeControllers.delete(component);
  }
}

function hasActiveController(component) {
  return Boolean(component && activeControllers.has(component));
}

function releaseBusyState(sourceId, originalComponent) {
  const nextComponent = currentComponent(sourceId, originalComponent);
  if (!nextComponent) return;
  if (hasActiveController(originalComponent) || hasActiveController(nextComponent)) return;

  setBusy(nextComponent, false);
}

async function updateDiscounts(component, discounts, sourceId, event = null) {
  const controller = abortPrevious(component);
  const requestMarker = cartPerformance.createStartingMarker('discount-update:request');

  try {
    const response = await fetch(`${routesRoot}cart/update.js`, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        discount: discounts.join(','),
        ...cartSectionsPayload(),
      }),
    });

    if (!response.ok) throw new Error('Unable to update discount.');

    const data = await response.json();
    cartPerformance.measureFromMarker(requestMarker);
    if (event) cartPerformance.measureFromEvent('discount-update:user-action', event);

    document.dispatchEvent(
      new DiscountUpdateEvent(data, sourceId, {
        sections: data.sections || {},
        itemCount: data.item_count,
        discounts,
      }),
    );

    return data;
  } finally {
    clearController(component, controller);
  }
}

async function applyDiscount(form, event) {
  const component = form.closest(selectors.component);
  const input = form.querySelector(selectors.input);
  if (!component || !(input instanceof HTMLInputElement)) return;

  const code = input.value.trim();
  if (!code) return;

  const sourceId = component.id || component.closest('[id]')?.id || 'cart-discount';
  const discounts = existingDiscounts(component);
  if (discounts.includes(code)) {
    input.value = '';
    return;
  }

  setError(component, '');
  setBusy(component, true);

  try {
    const data = await updateDiscounts(component, [...discounts, code], sourceId, event);
    const invalid = data.discount_codes?.find((discount) => discount.code === code && discount.applicable === false);
    const nextComponent = currentComponent(sourceId, component);
    if (invalid) {
      setError(nextComponent, 'This discount code is not valid for this cart.');
      const nextInput = nextComponent?.querySelector(selectors.input);
      if (nextInput instanceof HTMLInputElement) nextInput.value = '';
    }
  } catch (error) {
    if (error.name !== 'AbortError') {
      const message = error.message || 'Unable to apply discount.';
      setError(currentComponent(sourceId, component), message);
      document.dispatchEvent(new CartErrorEvent(sourceId, message));
    }
  } finally {
    releaseBusyState(sourceId, component);
  }
}

async function removeDiscount(pill, event) {
  const component = pill.closest(selectors.component);
  if (!component) return;

  const code = pill.dataset.discountCode;
  const discounts = existingDiscounts(component).filter((discount) => discount !== code);
  const sourceId = component.id || component.closest('[id]')?.id || 'cart-discount';

  setError(component, '');
  setBusy(component, true);

  try {
    await updateDiscounts(component, discounts, sourceId, event);
  } catch (error) {
    if (error.name !== 'AbortError') {
      const message = error.message || 'Unable to remove discount.';
      setError(currentComponent(sourceId, component), message);
      document.dispatchEvent(new CartErrorEvent(sourceId, message));
    }
  } finally {
    releaseBusyState(sourceId, component);
  }
}

document.addEventListener('submit', (event) => {
  if (!(event.target instanceof HTMLFormElement)) return;
  if (!event.target.matches(selectors.form)) return;

  event.preventDefault();
  applyDiscount(event.target, event);
});

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const pill = event.target.closest(selectors.pill);
  if (!pill) return;

  event.preventDefault();
  removeDiscount(pill, event);
});
